/** Authenticated calls for the signed-in user's patients and files. */

async function request(path, { method = 'GET', body, isForm = false } = {}) {
  const token = window.localStorage.getItem('token');
  const headers = {};
  if (token) headers.Authorization = `Bearer ${token}`;
  if (body && !isForm) headers['Content-Type'] = 'application/json';
  const response = await fetch(path, {
    method,
    headers,
    body: body ? (isForm ? body : JSON.stringify(body)) : undefined,
  });
  if (response.status === 204) return null;
  const isJson = (response.headers.get('content-type') || '').includes('application/json');
  const payload = isJson ? await response.json().catch(() => ({})) : null;
  if (!response.ok) {
    const error = new Error(payload?.error || 'Request failed.');
    error.status = response.status;
    throw error;
  }
  if (!isJson) return response.blob();
  return payload;
}

export function listMyPatients({ limit = 50, offset = 0, q = '' } = {}) {
  const params = new URLSearchParams();
  params.set('limit', String(limit));
  params.set('offset', String(offset));
  if (q) params.set('q', q);
  return request(`/api/my-patients?${params}`);
}

export function getMyPatient(id) {
  return request(`/api/my-patients/${encodeURIComponent(id)}`);
}

export function createMyPatient(patient) {
  return request('/api/my-patients', { method: 'POST', body: patient });
}

export function saveMyPatient(id, patient) {
  return request(`/api/my-patients/${encodeURIComponent(id)}`, { method: 'PUT', body: patient });
}

export function deleteMyPatient(id) {
  return request(`/api/my-patients/${encodeURIComponent(id)}`, { method: 'DELETE' });
}

export function listMetahumanCharacterOptions() {
  return request('/api/my-patients/character-options');
}

export function getIntegratedPatientTemplate(personaId) {
  const params = new URLSearchParams();
  params.set('personaId', String(personaId));
  return request(`/api/my-patients/integrated-template?${params}`);
}

export function listMyPatientTemplates({ type, limit = 100, offset = 0, q = '' } = {}) {
  const params = new URLSearchParams();
  if (type) params.set('type', type);
  params.set('limit', String(limit));
  params.set('offset', String(offset));
  if (q) params.set('q', q);
  return request(`/api/my-patients/templates?${params}`);
}

export function createMyPatientTemplate(template) {
  return request('/api/my-patients/templates', { method: 'POST', body: template });
}

export function deleteMyPatientTemplate(id) {
  return request(`/api/my-patients/templates/${encodeURIComponent(id)}`, { method: 'DELETE' });
}

export function listMyFiles() {
  return request('/api/my-files');
}

export function createMyFolder(folder) {
  return request('/api/my-files/folders', { method: 'POST', body: folder });
}

export function saveMyFolder(id, folder) {
  return request(`/api/my-files/folders/${encodeURIComponent(id)}`, { method: 'PUT', body: folder });
}

export function deleteMyFolder(id) {
  return request(`/api/my-files/folders/${encodeURIComponent(id)}`, { method: 'DELETE' });
}

export function uploadMyFile({ file, fileTag, fileType, folderId, customVpfSourcePath, name }) {
  const body = new FormData();
  body.append('file', file, file.name);
  body.append('fileTag', fileTag);
  body.append('fileType', fileType);
  body.append('folderId', folderId || '');
  body.append('customVpfSourcePath', customVpfSourcePath || '');
  body.append('name', name || file.name);
  return request('/api/my-files/files', { method: 'POST', body, isForm: true });
}

export function saveMyFile(id, file) {
  return request(`/api/my-files/files/${encodeURIComponent(id)}`, { method: 'PUT', body: file });
}

export function replaceMyFile(id, file) {
  const body = new FormData();
  body.append('file', file, file.name);
  return request(`/api/my-files/files/${encodeURIComponent(id)}/content`, { method: 'PUT', body, isForm: true });
}

export function deleteMyFile(id) {
  return request(`/api/my-files/files/${encodeURIComponent(id)}`, { method: 'DELETE' });
}

export function downloadMyFile(id) {
  return request(`/api/my-files/files/${encodeURIComponent(id)}/content`);
}
